/**
 * staffExamPinStore — Staff Exam PIN inventory slice (Zustand).
 *
 *   GET    /api/staff/exam-pins              — list
 *   GET    /api/staff/exam-pins/:id          — detail (masked)
 *   GET    /api/staff/exam-pins/deliveries   — delivery log (SENT/FAILED)
 *   GET    /api/staff/exam-pins/stats        — inventory statistics
 *   POST   /api/staff/exam-pins?examType=…   — upload ({ pins: string[] })
 *   DELETE /api/staff/exam-pins/:id          — delete an unused AVAILABLE PIN
 */
import { apiRequest, normalizeList } from "./staffUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const staffExamPinInitialState = {
  staffExamPins: [],
  staffExamPinsLoading: false,
  staffExamPinsError: null,
  staffExamPinsTotal: 0,
  staffExamPinsPages: 1,

  staffExamPinDetail: null,
  staffExamPinDetailLoading: false,
  staffExamPinDetailError: null,

  staffExamPinDeliveries: [],
  staffExamPinDeliveriesLoading: false,
  staffExamPinDeliveriesError: null,
  staffExamPinDeliveriesTotal: 0,
  staffExamPinDeliveriesPages: 1,

  staffExamPinStats: null,
  staffExamPinStatsLoading: false,
  staffExamPinStatsError: null,

  staffExamPinMutationLoading: false,
  staffExamPinMutationError: null,
};

export function createStaffExamPinActions(set, get) {
  async function fetchList(prefix, path, params = {}, keys = []) {
    set({ [`${prefix}Loading`]: true, [`${prefix}Error`]: null });
    try {
      const query = new URLSearchParams(params).toString();
      const data = await apiRequest(get, `${path}${query ? `?${query}` : ""}`);
      const { items, total, totalPages } = normalizeList(data, keys);
      set({
        [`${prefix}Loading`]: false,
        [prefix]: items,
        [`${prefix}Total`]: total,
        [`${prefix}Pages`]: totalPages,
      });
      return { success: true, items, total, totalPages };
    } catch (err) {
      const msg = toDisplayString(err.message, "Failed to fetch Exam PINs");
      set({ [`${prefix}Loading`]: false, [`${prefix}Error`]: msg });
      return { success: false, error: msg };
    }
  }

  async function mutate(path, method, body, successMessage) {
    set({ staffExamPinMutationLoading: true, staffExamPinMutationError: null });
    try {
      const data = await apiRequest(get, path, { method, body });
      set({
        staffExamPinMutationLoading: false,
        staffExamPinMutationError: null,
        successMessage,
      });
      return { success: true, data: data?.data ?? data };
    } catch (err) {
      const msg = toDisplayString(err.message, "Action failed");
      set({ staffExamPinMutationLoading: false, staffExamPinMutationError: msg });
      return { success: false, error: msg };
    }
  }

  return {
    // GET /api/staff/exam-pins
    fetchStaffExamPins: (params = {}) =>
      fetchList("staffExamPins", "/api/staff/exam-pins", params, ["examPins", "pins"]),

    // GET /api/staff/exam-pins/deliveries
    fetchStaffExamPinDeliveries: (params = {}) =>
      fetchList("staffExamPinDeliveries", "/api/staff/exam-pins/deliveries", params, ["deliveries"]),

    // GET /api/staff/exam-pins/:id
    fetchStaffExamPin: async (id) => {
      set({ staffExamPinDetailLoading: true, staffExamPinDetailError: null });
      try {
        const data = await apiRequest(get, `/api/staff/exam-pins/${id}`);
        const detail = data?.data ?? data?.examPin ?? data;
        set({ staffExamPinDetailLoading: false, staffExamPinDetail: detail });
        return { success: true, data: detail };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch Exam PIN");
        set({ staffExamPinDetailLoading: false, staffExamPinDetailError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/staff/exam-pins/stats
    fetchStaffExamPinStats: async (params = {}) => {
      set({ staffExamPinStatsLoading: true, staffExamPinStatsError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const data = await apiRequest(
          get,
          `/api/staff/exam-pins/stats${query ? `?${query}` : ""}`
        );
        const stats = data?.data ?? data?.stats ?? data;
        set({ staffExamPinStatsLoading: false, staffExamPinStats: stats });
        return { success: true, stats };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch statistics");
        set({ staffExamPinStatsLoading: false, staffExamPinStatsError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/staff/exam-pins?examType=WAEC|NECO|NABTEB
    uploadStaffExamPins: ({ pins, examType }) =>
      mutate(
        `/api/staff/exam-pins?examType=${encodeURIComponent(examType)}`,
        "POST",
        { pins },
        "Exam PINs uploaded successfully"
      ),

    // DELETE /api/staff/exam-pins/:id
    deleteStaffExamPin: (id) =>
      mutate(`/api/staff/exam-pins/${id}`, "DELETE", undefined, "Exam PIN deleted"),
  };
}

const staffExamPinActions = createStaffExamPinActions;
export default staffExamPinActions;
